import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useQuery } from "../../../hooks/useQuery";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { ConnectorDropdown, Row } from "../../ConnectorDropdown";
import { navigate, routes } from "../../../lib/urls";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { PopoverManageRow } from "../PopoverManageRow";
import { OAuthAppModal, type OAuthAppValues } from "./OAuthAppModal";

// Tenant-admin management of OAuth apps (tier 1): register/edit/enable/delete
// the clients that a service account manager may then authorize per account.
// Mirrors IdentityProvidersButton; lives in the Service accounts tab header.
export function OAuthAppsButton() {
  const z = useZero();
  const [apps] = useQuery(queries.oauthApps());
  const [showRegister, setShowRegister] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const register = (values: OAuthAppValues) => {
    z.mutate(sharedMutators.registerOAuthApp({ id: nanoid(12), ...values }));
  };

  return (
    <>
      <ConnectorDropdown
        searchPlaceholder="Search OAuth apps…"
        opener={
          <Tooltip content="OAuth apps">
            <Button size="l" variant="secondary" icon="id-card-lanyard" />
          </Tooltip>
        }
      >
        {(query, close) => {
          const needle = query.trim().toLowerCase();
          const filtered = needle
            ? apps.filter((a) => a.name.toLowerCase().includes(needle))
            : apps;
          return (
            <>
              {filtered.length === 0 ? (
                <div style={{ padding: "8px 8px 12px" }}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {needle ? "No matches." : "No OAuth apps yet."}
                  </Text>
                </div>
              ) : (
                filtered.map((a) => (
                  <PopoverManageRow
                    key={a.id}
                    icon="id-card-lanyard"
                    title={a.name}
                    subtitle={(a.redirectUris ?? []).join(", ")}
                    dimmed={!!a.disabled}
                    onClick={() => {
                      close();
                      navigate(routes.modal.oauthApp, { id: a.id });
                    }}
                    menuItems={[
                      {
                        left: <Icon name="pencil" size={16} />,
                        label: "Open",
                        onClick: () => {
                          close();
                          navigate(routes.modal.oauthApp, { id: a.id });
                        },
                      },
                      {
                        left: (
                          <Icon
                            name={a.disabled ? "check" : "circle-off"}
                            size={16}
                          />
                        ),
                        label: a.disabled ? "Enable" : "Disable",
                        onClick: () =>
                          z.mutate(
                            sharedMutators.updateOAuthApp({
                              id: a.id,
                              disabled: !a.disabled,
                            })
                          ),
                      },
                      {
                        left: <Icon name="trash" size={16} />,
                        label: "Delete",
                        onClick: () => {
                          close();
                          setConfirmDelete({ id: a.id, name: a.name });
                        },
                      },
                    ]}
                  />
                ))
              )}
              <Row
                onClick={() => {
                  close();
                  setShowRegister(true);
                }}
              >
                <Icon name="plus" size={16} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text variant="text-regular" color="var(--text-primary)">
                    Register OAuth app
                  </Text>
                  <Text
                    ellipsis
                    variant="text-small"
                    color="var(--text-secondary)"
                  >
                    Let an app obtain credentials for a service account
                  </Text>
                </div>
              </Row>
            </>
          );
        }}
      </ConnectorDropdown>

      {showRegister && (
        <OAuthAppModal
          onClose={() => setShowRegister(false)}
          onSubmit={register}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            z.mutate(sharedMutators.deleteOAuthApp({ id: confirmDelete.id }));
            setConfirmDelete(null);
          }}
          title="Delete OAuth app"
          description={`Removing “${confirmDelete.name}” immediately breaks every service account authorization and credential that uses it. This can’t be undone.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </>
  );
}
